/**
 * Row 1 of the Files pane, spanning content and rail: ‹ › · the icon breadcrumb · view options ·
 * the tree toggle · an optional close. Actions on the path live in row 2 ({@link DriveToolbar}).
 */
import {type DriveId} from "@agenta/entities/drive"
import {useLocalize} from "@agenta/i18n"
import {getShortcut, shortcutAria, shortcutText} from "@agenta/shared/utils"
import {ShortcutKeys} from "@agenta/ui/shortcuts"
import {
    Button,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    SimpleTooltip as Tooltip,
} from "@agenta/ui/ui"
import {
    ArrowsIn,
    ArrowsOut,
    CaretDoubleLeft,
    CaretDoubleRight,
    CaretLeft,
    CaretRight,
    ClockCountdown,
    Copy,
    EyeSlash,
    GitBranch,
    Sidebar,
    Sliders,
    WarningCircle,
    X,
} from "@phosphor-icons/react"

import {DriveBreadcrumb} from "./DriveBreadcrumb"
import {DriveRetryButton} from "./DriveFileRow"
import {SelectedMark} from "./DriveMenuMark"

/** The chrome's icon button: the kit's ghost icon-sm, muted until hover. */
export const ROW_ICON_BTN = "text-colorTextTertiary hover:text-colorText"
export const ROW_ICON_BTN_ON = "bg-accent text-colorText"

export const DriveHeader = ({
    selectedPath,
    isFolder,
    rootLabel,
    onNavigate,
    canGoBack,
    canGoForward,
    onBack,
    onForward,
    copyText,
    ids,
    showOrigin,
    showTemporary,
    onToggleTemporary,
    showHidden,
    onToggleHidden,
    inGitScope,
    showGitignored,
    onToggleGitignored,
    treeVisible,
    onToggleTree,
    onClose,
    closeVariant = "close",
    expanded,
    onToggleExpand,
    expandPlacement = "leading",
    partialErrored,
    onRetry,
    retrying,
}: {
    selectedPath: string | null
    isFolder: boolean
    rootLabel: string
    onNavigate: (path: string) => void
    canGoBack: boolean
    canGoForward: boolean
    onBack: () => void
    onForward: () => void
    copyText: (text: string, successMessage?: string) => void
    /** Raw ids for the inspector; empty when it is off. */
    ids: DriveId[]
    /** The drive mixes agent and session files, so "Show temporary files" applies. */
    showOrigin: boolean
    showTemporary: boolean
    onToggleTemporary: () => void
    showHidden: boolean
    onToggleHidden: () => void
    inGitScope: boolean
    showGitignored: boolean
    onToggleGitignored: () => void
    treeVisible: boolean
    onToggleTree: () => void
    /** For hosts whose close lives in this row. */
    onClose?: () => void
    /** `back` is the phone's: the pane has the whole screen, so its leave control sits where a
     * back control does — first in the row, pointing back. */
    closeVariant?: "close" | "collapse" | "back"
    expanded?: boolean
    onToggleExpand?: () => void
    expandPlacement?: "leading" | "before-options"
    partialErrored?: boolean
    onRetry?: () => void
    retrying?: boolean
}) => {
    const t = useLocalize()
    const expandLabel = t(
        expandPlacement === "before-options"
            ? expanded
                ? "com_entity_drive_restore_pane"
                : "com_entity_drive_expand_pane"
            : expanded
              ? "com_entity_drive_collapse_drawer"
              : "com_entity_drive_expand_drawer",
    )
    const expandButton = onToggleExpand ? (
        <Tooltip title={expandLabel}>
            <Button
                variant="ghost"
                size="icon-sm"
                aria-label={expandLabel}
                aria-pressed={expanded}
                onClick={onToggleExpand}
                className={ROW_ICON_BTN}
            >
                {expanded ? <ArrowsIn size={15} /> : <ArrowsOut size={15} />}
            </Button>
        </Tooltip>
    ) : null
    return (
        // The session bar's height and border token, so its line continues across the divider.
        <div className="flex h-[48px] shrink-0 items-center gap-1.5 border-0 border-b border-solid border-[var(--ag-surface-card-border)] px-2">
            {onClose && closeVariant === "close" ? (
                <Tooltip title={t("com_ui_close")}>
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t("com_ui_close")}
                        onClick={onClose}
                        className={ROW_ICON_BTN}
                    >
                        <X size={15} />
                    </Button>
                </Tooltip>
            ) : null}
            {onClose && closeVariant === "back" ? (
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-keyshortcuts={shortcutAria("panel.files")}
                    aria-label={t("com_entity_drive_close_pane")}
                    onClick={onClose}
                    className={ROW_ICON_BTN}
                >
                    <CaretDoubleLeft size={15} />
                </Button>
            ) : null}
            {expandPlacement === "leading" ? expandButton : null}
            <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("com_ui_back")}
                title={t("com_entity_drive_back_shortcut", {
                    shortcut: shortcutText(getShortcut("drive.back")!),
                })}
                aria-keyshortcuts={shortcutAria("drive.back")}
                disabled={!canGoBack}
                onClick={onBack}
                className={ROW_ICON_BTN}
            >
                <CaretLeft size={15} weight="bold" />
            </Button>
            <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("com_entity_drive_forward")}
                title={t("com_entity_drive_forward_shortcut", {
                    shortcut: shortcutText(getShortcut("drive.forward")!),
                })}
                aria-keyshortcuts={shortcutAria("drive.forward")}
                disabled={!canGoForward}
                onClick={onForward}
                className={ROW_ICON_BTN}
            >
                <CaretRight size={15} weight="bold" />
            </Button>
            {/* The crumb takes the row's slack and scrolls sideways when a deep path outgrows it. */}
            <div className="ml-1 flex min-w-0 flex-1 items-center">
                <DriveBreadcrumb
                    variant="icons"
                    shown={selectedPath ?? ""}
                    rootLabel={rootLabel}
                    isFile={!isFolder}
                    onNavigate={onNavigate}
                />
            </div>
            {/* One mount failed but the drive still browses: a compact warning + retry. */}
            {partialErrored && onRetry ? (
                <Tooltip title={t("com_entity_drive_partial_error")}>
                    <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap">
                        <WarningCircle
                            size={14}
                            weight="fill"
                            className="shrink-0 text-colorWarning"
                        />
                        <DriveRetryButton onRetry={onRetry} busy={retrying} />
                    </span>
                </Tooltip>
            ) : null}
            {expandPlacement === "before-options" ? expandButton : null}
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t("com_entity_drive_view_options")}
                        title={t("com_entity_drive_view_options")}
                        className={ROW_ICON_BTN}
                    >
                        <Sliders size={15} />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[220px]">
                    {/* Plain items with a right-side check; `preventDefault` keeps the menu open. */}
                    {showOrigin ? (
                        <DropdownMenuItem
                            role="menuitemcheckbox"
                            aria-checked={showTemporary}
                            onSelect={(e) => {
                                e.preventDefault()
                                onToggleTemporary()
                            }}
                        >
                            <ClockCountdown />
                            {t("com_entity_drive_show_temporary")}
                            <SelectedMark on={showTemporary} />
                        </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem
                        role="menuitemcheckbox"
                        aria-checked={showHidden}
                        onSelect={(e) => {
                            e.preventDefault()
                            onToggleHidden()
                        }}
                    >
                        <EyeSlash />
                        {t("com_entity_drive_show_hidden")}
                        <SelectedMark on={showHidden} />
                    </DropdownMenuItem>
                    {inGitScope ? (
                        <DropdownMenuItem
                            role="menuitemcheckbox"
                            aria-checked={showGitignored}
                            onSelect={(e) => {
                                e.preventDefault()
                                onToggleGitignored()
                            }}
                        >
                            <GitBranch />
                            {t("com_entity_drive_show_gitignored")}
                            <SelectedMark on={showGitignored} />
                        </DropdownMenuItem>
                    ) : null}
                    {ids.length ? <DropdownMenuSeparator /> : null}
                    {ids.map((id) => (
                        <DropdownMenuItem
                            key={id.key}
                            onSelect={() =>
                                copyText(
                                    id.value,
                                    t("com_entity_drive_id_copied", {label: id.label}),
                                )
                            }
                        >
                            <Copy />
                            <span className="flex flex-col gap-0.5 py-0.5">
                                <span className="text-xs font-medium">
                                    {t("com_entity_drive_copy_id", {label: id.label})}
                                </span>
                                <span className="font-mono text-[12px] text-colorTextTertiary">
                                    {id.value}
                                </span>
                            </span>
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
            </DropdownMenu>
            <Tooltip
                title={t(treeVisible ? "com_entity_drive_hide_tree" : "com_entity_drive_show_tree")}
            >
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t("com_entity_drive_show_tree")}
                    aria-pressed={treeVisible}
                    onClick={onToggleTree}
                    className={treeVisible ? ROW_ICON_BTN_ON : ROW_ICON_BTN}
                >
                    {/* The app's sidebar glyph (the nav toggle's), as is — the panel it shows is
                        the idea, not which edge the tree docks on. */}
                    <Sidebar size={16} weight={treeVisible ? "fill" : "regular"} />
                </Button>
            </Tooltip>
            {onClose && closeVariant === "collapse" ? (
                <Tooltip
                    title={
                        <span className="flex items-center gap-1.5">
                            {t("com_entity_drive_collapse_files")}{" "}
                            <ShortcutKeys id="panel.files" tone="inverse" />
                        </span>
                    }
                >
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-keyshortcuts={shortcutAria("panel.files")}
                        aria-label={t("com_entity_drive_collapse_pane")}
                        onClick={onClose}
                        className={ROW_ICON_BTN}
                    >
                        <CaretDoubleRight size={15} />
                    </Button>
                </Tooltip>
            ) : null}
        </div>
    )
}
